// Formats a given time in seconds to MM:SS
function seconds2Time(totalSeconds = 0) {
  const seconds = Math.floor(totalSeconds) % 60;
  const minutes = Math.floor(totalSeconds / 60) % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

// Slider component for Audio songtrack and volume sliders
class AudioSlider {
  constructor($element, options = {}) {
    this.$element = $element;
    this.options = {
      classes: {
        input: 'audio-slider__input',
        sliderbar: 'audio-slider__slider-bar',
      },
      enableInput: false,
      mouseStart: 0,
      changeCallback: null,
      ...options,
    };

    this.init();
  }

  init() {
    // An html input element (type=range) to preserve semantics on the document.
    this.$input = this.$element.querySelector(`.${this.options.classes.input}`);

    // Register when the range input is changed e.g. by keyboard actions
    this.$input.addEventListener('change', this.inputChanged.bind(this));

    // The actually visible slider bar
    this.$sliderbar = this.$element.querySelector(`.${this.options.classes.sliderbar}`);

    // Register when mouse buttons or mobile screen are pressed OVER the slider bar
    // This is to register that user input is handled from now on.
    this.$sliderbar.addEventListener('mousedown', this.onmousedown.bind(this));
    this.$sliderbar.addEventListener('touchstart', this.onmousedown.bind(this));
    this.$sliderbar.addEventListener('click', this.onmousemove.bind(this));

    // Listen Mouse or touch movements.
    // Listeners are set to window for better use performance
    window.addEventListener('mousemove', this.onmousemove.bind(this));
    window.addEventListener('touchmove', this.onmousemove.bind(this));

    // Listen to the release of mouse buttons or mobile screen
    // to register that user input is not handled anymore.
    window.addEventListener('mouseup', this.onmouseup.bind(this));
    window.addEventListener('touchend', this.onmouseup.bind(this));

    // Set the initial value from the range input element
    this.setValue(this.$input.value);
  }

  updateSliderbar() {
    this.$sliderbar.style.setProperty('--slider-position', `${this.$input.value}%`);
  }

  inputChanged() {
    this.updateSliderbar();

    if (this.options.changeCallback) {
      this.options.changeCallback();
    }
  }

  onmousedown() {
    // Register that user input is handled from now on
    // and set the start position for the percent-value calculation
    this.options.enableInput = true;
    this.options.mouseStart = this.$sliderbar.getBoundingClientRect().left;
  }

  onmousemove(event) {
    if (this.options.enableInput) {
      const { mouseStart } = this.options;
      const { clientWidth } = this.$sliderbar;
      const { clientX } = (event.touches && event.touches[0]) || event;

      // Calculate percentage from mouse position relative to the sliderbar
      const percent = (clientX - mouseStart) / clientWidth;

      // Clip percentage between 0 and 1
      const percentClipped = Math.max(0, Math.min(percent, 1));

      // Set the calculated value to the range input element
      // and call the given callback if any
      this.setValue(100 * percentClipped);
      if (this.options.changeCallback) {
        this.options.changeCallback();
      }
    }
  }

  onmouseup() {
    // Register that user input is ignored from now on
    // to prevent further mouse move or touch events
    this.options.enableInput = false;
  }

  getValue() {
    return this.$input.value;
  }

  setValue(value) {
    this.$input.value = value;
    this.updateSliderbar();
  }

  enable() {
    this.$input.disabled = false;
    this.$element.classList.remove('audio-slider--disabled');
  }

  disable() {
    this.$input.disabled = true;
    this.$element.classList.add('audio-slider--disabled');
  }

  isDisabled() {
    return this.$input.disabled;
  }
}

// Actual audio player component class
export default class AudioPlayer {
  constructor($element, options = {}) {
    this.$element = $element;

    this.options = {
      classes: {
        player: 'audio__player',
        playpause: 'audio__control--playpause',
        songslider: 'audio__slider--songtrack',
        volumeslider: 'audio__slider--volume',
        currentTimeText: 'audio__time--current',
        durationTimeText: 'audio__time--duration',
        mutebutton: 'audio__control--mute',
      },
      intialized: false,
      ...options,
    };

    this.init();
    // this.initListeners();
  }

  init() {
    // Find player and control elements
    this.$player = this.$element.querySelector(`.${this.options.classes.player}`);
    this.$playbutton = this.$element.querySelector(`.${this.options.classes.playpause}`);
    this.$mutebutton = this.$element.querySelector(`.${this.options.classes.mutebutton}`);
    this.$songslider = this.$element.querySelector(`.${this.options.classes.songslider}`);
    this.$volumeslider = this.$element.querySelector(`.${this.options.classes.volumeslider}`);
    this.$currentTimeText = this.$element.querySelector(`.${this.options.classes.currentTimeText}`);
    this.$durationText = this.$element.querySelector(`.${this.options.classes.durationTimeText}`);

    // If any button is found, find their icon
    if (this.$playbutton) {
      this.$playbuttonIcon = this.$playbutton.querySelector('.icon');
    }
    if (this.$mutebutton) {
      this.$mutebuttonIcon = this.$mutebutton.querySelector('.icon');
    }

    this.initListeners();
  }

  initListeners() {
    // Set event listeners if the player can play back the audio file.
    if (!this.options.intialized) {
      if (this.$playbutton) {
        this.$playbutton.addEventListener('click', this.playpauseClick.bind(this));
      }
      if (this.$mutebutton) {
        this.$mutebutton.addEventListener('click', this.mutebuttonClick.bind(this));
      }

      // Set event listeners for the player...
      // ...when progress or seek
      this.$player.addEventListener('timeupdate', this.timeupdate.bind(this));
      // ...when playback starts
      this.$player.addEventListener('play', this.onplay.bind(this));
      // ...or playback stops or is paused by the user
      this.$player.addEventListener('pause', this.onpause.bind(this));

      // Make an interactive range slider for the songslider element
      if (this.$songslider) {
        this.songslider = new AudioSlider(this.$songslider, {
          changeCallback: this.songpositionChange.bind(this),
        });
      }

      // Make an interactive range slider for the volumeslider element
      if (this.$volumeslider) {
        this.volumeslider = new AudioSlider(this.$volumeslider, {
          changeCallback: this.volumeChange.bind(this),
        });
      }
    }

    window.addEventListener('load', () => {
      // initialy display the audio duration
      this.$durationText.innerHTML = seconds2Time(this.$player.duration);
      // Register that the audio player component is now initialized
      // which means it is ready to play back
      this.options.intialized = true;
      this.timeupdate();
    });
  }

  play() {
    this.$player.play();
  }

  pause() {
    this.$player.pause();
  }

  setVolume(volume) {
    this.$player.volume = volume / 100;
  }

  getVolume() {
    const { volume } = this.$player;
    return volume * 100;
  }

  setSongPosition(percent = 0) {
    const { duration } = this.$player;
    this.$player.currentTime = percent * duration / 100;
  }

  getSongPosition() {
    const { currentTime, duration } = this.$player;
    return 100 * currentTime / duration;
  }

  playpauseClick() {
    if (this.$player.paused) {
      this.play();
    } else {
      this.pause();
    }
  }

  mutebuttonClick() {
    const $muteIconUse = this.$mutebuttonIcon.querySelector('use');

    if (this.$player.muted) {
      this.$player.muted = false;
      this.volumeslider.enable();
      this.$mutebuttonIcon.classList.remove('icon--muted');
      this.$mutebuttonIcon.classList.add('icon--volume');
      $muteIconUse.setAttribute('xlink:href', '#icon-volume');
    } else {
      this.$player.muted = true;
      this.volumeslider.disable();
      this.$mutebuttonIcon.classList.remove('icon--volume');
      this.$mutebuttonIcon.classList.add('icon--mute');
      $muteIconUse.setAttribute('xlink:href', '#icon-mute');
    }
  }

  songpositionChange() {
    this.setSongPosition(this.songslider.getValue());
  }

  volumeChange() {
    this.setVolume(this.volumeslider.getValue());
  }

  timeupdate() {
    if (this.songslider) {
      this.songslider.setValue(this.getSongPosition());
      this.$currentTimeText.innerHTML = seconds2Time(this.$player.currentTime);
    }
  }

  onplay() {
    const $playIconUse = this.$playbuttonIcon.querySelector('use');
    this.$element.classList.add('audio--playing');
    this.$playbuttonIcon.classList.remove('icon--play');
    this.$playbuttonIcon.classList.add('icon--pause');
    $playIconUse.setAttribute('xlink:href', '#icon-pause');
  }

  onpause() {
    const $playIconUse = this.$playbuttonIcon.querySelector('use');
    this.$element.classList.remove('audio--playing');
    this.$playbuttonIcon.classList.remove('icon--pause');
    this.$playbuttonIcon.classList.add('icon--play');
    $playIconUse.setAttribute('xlink:href', '#icon-play');
  }
}

document.querySelectorAll('.js-audio').forEach(
  $audio => new AudioPlayer($audio),
);
